<template>
  <ul
    class="tag-list"
    :class="{ 'desktop-mode': apiConfig.viewerMode === 'desktop' }"
  >
    <li
      v-for="tag in tags"
      :key="tag.name"
      class="tag"
      :class="{ deleted: tag.deleted, 'is-open': openTagName === tag.name }"
    >
      <button
        :ref="(element) => setTagTriggerRef(tag.name, element as Element | null)"
        class="tag-trigger"
        type="button"
        :aria-expanded="openTagName === tag.name ? 'true' : 'false'"
        @click.stop="toggleTagBubble(tag.name)"
      >
        <span
          class="tag-label"
        >
          {{ formatTagName(tag.name) }}
        </span>
        <span
          v-if="tag.calculated"
          class="tag-indicator"
          title="Calculated tag"
          aria-hidden="true"
        >
          <Link2
            :size="12"
          />
        </span>
      </button>
    </li>
  </ul>

  <Teleport
    to="body"
  >
    <div
      v-if="openTag"
      ref="bubbleEl"
      class="tag-action-bubble"
      :style="bubbleStyle"
      @click.stop
    >
      <button
        class="bubble-action"
        type="button"
        title="Copy tag"
        aria-label="Copy tag"
        @click.stop="handleCopy(openTag.name)"
      >
        <Copy
          :size="16"
        />
      </button>
      <button
        class="bubble-action"
        type="button"
        title="Search this tag"
        aria-label="Search this tag"
        @click.stop="handleSearch(openTag.name)"
      >
        <Search
          :size="16"
        />
      </button>
      <button
        class="bubble-action"
        :class="{ 'fav-active': favoriteTags.isFavorite(openTag.name) }"
        type="button"
        :title="favoriteTags.isFavorite(openTag.name) ? 'Remove from favorites' : 'Add to favorites'"
        :aria-label="favoriteTags.isFavorite(openTag.name) ? 'Remove from favorites' : 'Add to favorites'"
        @click.stop="favoriteTags.toggle(openTag.name)"
      >
        <Star
          :size="16"
          :fill="favoriteTags.isFavorite(openTag.name) ? 'currentColor' : 'none'"
        />
      </button>
      <button
        v-if="canDelete(openTag)"
        class="bubble-action delete-action"
        type="button"
        title="Delete tag"
        aria-label="Delete tag"
        @click.stop="handleDelete(openTag.name)"
      >
        <Trash2
          :size="16"
        />
      </button>
      <button
        v-if="canRestore(openTag)"
        class="bubble-action"
        type="button"
        title="Restore tag"
        aria-label="Restore tag"
        @click.stop="handleRestore(openTag.name)"
      >
        <RotateCcw
          :size="16"
        />
      </button>
      <button
        class="bubble-action close-action"
        type="button"
        title="Close tag actions"
        aria-label="Close tag actions"
        @click.stop="closeBubble()"
      >
        <X
          :size="16"
        />
      </button>
    </div>
  </Teleport>
</template>

<script lang="ts">
import type { Metadata } from "@/stores/ImageMetadata";

export type Props = {
  meta?: Metadata | null;
};
export type Emits = {
  (e: "delete", tag: string): void;
  (e: "add", tag: string): void;
};
</script>
<script setup lang="ts">
/* eslint-disable import/first */
import type { TagMeta } from "@/components/meta/types";
import { computed, defineEmits, defineProps, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import { useApiStore, SpecialPage } from "@/stores/api";
import { TagState } from "@/stores/ImageMetadata";
import { Copy, Link2, RotateCcw, Search, Star, Trash2, X } from "lucide-vue-next";
import { useFavoriteTagsStore } from "@/stores/favoriteTags";
/* eslint-enable import/first */

const favoriteTags = useFavoriteTagsStore();

const apiConfig = useApiStore();

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const supportsSplitterCharacter = CSS.supports("hyphenate-character: '#'");
const splitterCharacter = supportsSplitterCharacter ? "\u00AD" : "​";
const openTagName = ref<string | null>(null);
const bubbleEl = ref<HTMLDivElement | null>(null);
const bubbleStyle = ref<Record<string, string>>({ visibility: "hidden" });
const tagTriggerRefs = ref<Record<string, HTMLButtonElement | null>>({});
let bubblePositionAnimationFrame: number | null = null;

const tags = computed<TagMeta[]>(() =>
  !props.meta
    ? []
    : (props.meta.tags[apiConfig.tagReadServiceId].display_tags[TagState.CURRENT] ?? []).map(
        (tag: string): TagMeta => {
          const deleted = (
            props.meta?.tags[apiConfig.tagReadServiceId]?.storage_tags[TagState.DELETED] ?? []
          ).includes(tag);
          const calculated = !(
            props.meta?.tags[apiConfig.tagReadServiceId]?.storage_tags[TagState.CURRENT] ?? []
          ).includes(tag);
          const written = !(
            props.meta?.tags[apiConfig.tagWriteServiceId]?.storage_tags[TagState.CURRENT] ?? []
          ).includes(tag);
          const writtenDeleted = (
            props.meta?.tags[apiConfig.tagWriteServiceId]?.storage_tags[TagState.DELETED] ?? []
          ).includes(tag);

          return {
            name: tag,
            deleted,
            calculated: calculated && deleted,
            written: written && writtenDeleted,
          };
        },
      ),
);
const openTag = computed<TagMeta | null>(() =>
  tags.value.find((tag) => tag.name === openTagName.value) ?? null,
);

watch(openTagName, async (tagName) => {
  if (tagName === null) {
    bubbleStyle.value = { visibility: "hidden" };
    return;
  }

  bubbleStyle.value = { visibility: "hidden" };
  await nextTick();
  scheduleBubblePosition();
});

watch(
  () => tags.value.map((tag) => `${tag.name}:${tag.deleted}:${tag.calculated}:${tag.written}`).join("|"),
  async () => {
    if (openTagName.value === null) return;
    if (!openTag.value) {
      closeBubble();
      return;
    }
    await nextTick();
    scheduleBubblePosition();
  },
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleDocumentKeydown);
  window.addEventListener("resize", handleViewportChange);
  window.addEventListener("scroll", handleViewportChange, true);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleDocumentKeydown);
  window.removeEventListener("resize", handleViewportChange);
  window.removeEventListener("scroll", handleViewportChange, true);
  if (bubblePositionAnimationFrame !== null) {
    window.cancelAnimationFrame(bubblePositionAnimationFrame);
  }
});

function formatTagName(tagName: string): string {
  return tagName
    .replace(
      / /g,
      supportsSplitterCharacter ? " " : "_",
    )
    .replace(
      /(:|_| |-|\)|\/)/g,
      `$1${splitterCharacter}`,
    );
}

function setTagTriggerRef(tagName: string, element: Element | null) {
  if (element instanceof HTMLButtonElement) {
    tagTriggerRefs.value[tagName] = element;
    return;
  }

  delete tagTriggerRefs.value[tagName];
}

function toggleTagBubble(tagName: string) {
  openTagName.value = openTagName.value === tagName ? null : tagName;
}

function closeBubble() {
  openTagName.value = null;
}

function canDelete(tag: TagMeta): boolean {
  return !tag.calculated && !tag.written && !tag.deleted;
}

function canRestore(tag: TagMeta): boolean {
  return tag.deleted && !tag.calculated;
}

function scheduleBubblePosition() {
  if (bubblePositionAnimationFrame !== null) {
    window.cancelAnimationFrame(bubblePositionAnimationFrame);
  }

  bubblePositionAnimationFrame = window.requestAnimationFrame(() => {
    bubblePositionAnimationFrame = null;
    positionBubble();
  });
}

function positionBubble() {
  if (openTagName.value === null) return;

  const triggerEl = tagTriggerRefs.value[openTagName.value];
  const bubble = bubbleEl.value;
  if (!triggerEl || !bubble) return;

  const triggerRect = triggerEl.getBoundingClientRect();
  const bubbleRect = bubble.getBoundingClientRect();
  const viewportPadding = 12;
  const gap = 10;
  const maxLeft = Math.max(viewportPadding, window.innerWidth - bubbleRect.width - viewportPadding);
  const maxTop = Math.max(viewportPadding, window.innerHeight - bubbleRect.height - viewportPadding);

  let left = triggerRect.left + (triggerRect.width / 2) - (bubbleRect.width / 2);
  left = Math.min(Math.max(viewportPadding, left), maxLeft);

  const preferredTop = triggerRect.top - bubbleRect.height - gap;
  const fallbackTop = triggerRect.bottom + gap;
  const top = Math.min(
    Math.max(viewportPadding, preferredTop >= viewportPadding ? preferredTop : fallbackTop),
    maxTop,
  );

  bubbleStyle.value = {
    left: `${left}px`,
    top: `${top}px`,
    visibility: "visible",
  };
}

function handleViewportChange() {
  if (openTagName.value === null) return;
  scheduleBubblePosition();
}

function handleClickOutside(event: MouseEvent) {
  if (openTagName.value === null) return;

  const target = event.target as Node | null;
  if (!target) return;
  if (bubbleEl.value?.contains(target)) return;
  if (Object.values(tagTriggerRefs.value).some((element) => element?.contains(target))) return;
  closeBubble();
}

function handleDocumentKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  closeBubble();
}

function copyTag(tag: string) {
  navigator.clipboard.writeText(tag);
}

function appendToSearch(tag: string) {
  apiConfig.selectedPage = SpecialPage.SEARCH;
  const current = apiConfig.searchQuery ?? "";
  apiConfig.searchQuery = current ? `${current}, ${tag}` : tag;
}

function handleCopy(tag: string) {
  copyTag(tag);
  closeBubble();
}

function handleSearch(tag: string) {
  appendToSearch(tag);
  closeBubble();
}

function handleDelete(tag: string) {
  emit("delete", tag);
  closeBubble();
}

function handleRestore(tag: string) {
  emit("add", tag);
  closeBubble();
}
</script>

<style lang="scss" scoped src="@/components/meta/meta-shared.scss" />
<style scoped lang="scss">
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.65rem;

  /* Desktop (horizontal scroll): each tag on its own line */
  &.desktop-mode {
    flex-direction: column;
    align-items: flex-start;
  }
}

.tag {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0;
  min-height: 0;

  &.deleted .tag-trigger {
    opacity: 0.72;
    text-decoration: line-through;
    text-decoration-thickness: 1.5px;
  }
}

.tag-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 2rem;
  max-width: min(100%, 100vw - 2rem);
  padding: 0.25rem 0.65rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--text-accent);
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.16em;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover,
  &:focus-visible,
  .tag.is-open & {
    background: rgba(255, 255, 255, 0.08);
    color: var(--accent);
    outline: none;
  }
}

.tag-label {
  line-height: 1.15;
  overflow-wrap: break-word;
}

.tag-indicator {
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.68);
  flex-shrink: 0;
}

.tag-action-bubble {
  position: fixed;
  z-index: 2200;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  max-width: calc(100vw - 24px);
  padding: 0.3rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(17, 19, 24, 0.96);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(14px);
}

.bubble-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.1);
    color: var(--accent);
    outline: none;
    transform: translateY(-1px);
  }

  &.fav-active {
    color: #f5c94c;
  }
}

.delete-action:hover,
.delete-action:focus-visible,
.close-action:hover,
.close-action:focus-visible {
  color: #ff7a7a;
}
</style>
